import React, { useEffect, useState } from "react";
import { CaretUp, CaretDown, CaretRight } from "@phosphor-icons/react";
import api, { reorderRequestTypes } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

export default function RequestTypeList() {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [open, setOpen] = useState({});          // id -> bool
  const [subLabel, setSubLabel] = useState({});  // id -> draft text

  const refresh = () => api.get("/request-types").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!label.trim()) return;
    try {
      await api.post("/request-types", { label: label.trim() });
      setLabel("");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/request-types/${id}`, body);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (it) => {
    try {
      await api.delete(`/request-types/${it.id}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try { await reorderRequestTypes(next.map((x) => x.id)); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to reorder"); refresh(); }
  };

  const addSub = (it) => {
    const draft = (subLabel[it.id] || "").trim();
    if (!draft) return;
    setSubLabel((s) => ({ ...s, [it.id]: "" }));
    patch(it.id, { sub_types: [...(it.sub_types || []), { label: draft }] });
  };
  const toggleSub = (it, value) =>
    patch(it.id, { sub_types: (it.sub_types || []).map((s) => (s.value === value ? { ...s, active: !s.active } : s)) });
  const removeSub = (it, value) =>
    patch(it.id, { sub_types: (it.sub_types || []).filter((s) => s.value !== value) });

  return (
    <div className="surface-card p-4" data-testid="request-type-list">
      <div className="font-medium text-slate-800">Request Types</div>
      <div className="mt-3 flex gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="Add request type…" onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button onClick={add}>Add</Button>
      </div>
      <div className="mt-3 divide-y divide-slate-100">
        {items.map((it, idx) => {
          const subs = it.sub_types || [];
          const isOpen = !!open[it.id];
          return (
            <div key={it.id} className="py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="flex flex-col">
                    <button className="text-slate-300 hover:text-slate-700 disabled:opacity-30" onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="Move up" title="Move up"><CaretUp size={12} weight="bold" /></button>
                    <button className="text-slate-300 hover:text-slate-700 disabled:opacity-30" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} aria-label="Move down" title="Move down"><CaretDown size={12} weight="bold" /></button>
                  </div>
                  <span className={`truncate ${it.active ? "text-slate-800" : "text-slate-400 line-through"}`}>{it.label}</span>
                  <button type="button" onClick={() => setOpen((o) => ({ ...o, [it.id]: !isOpen }))}
                    className="inline-flex items-center gap-0.5 text-[11px] text-slate-400 hover:text-slate-700 shrink-0"
                    data-testid={`subtypes-toggle-${it.value}`}>
                    <CaretRight size={10} weight="bold" className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    {subs.length ? `${subs.length} sub-type${subs.length === 1 ? "" : "s"}` : "sub-types"}
                  </button>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button className="text-xs text-slate-500 hover:text-slate-900" onClick={() => patch(it.id, { active: !it.active })}>
                    {it.active ? "Disable" : "Enable"}
                  </button>
                  <button className="text-xs text-rose-500 hover:text-rose-700" onClick={() => del(it)}>Delete</button>
                </div>
              </div>
              {isOpen && (
                <div className="mt-2 ml-6 space-y-2" data-testid={`subtypes-panel-${it.value}`}>
                  <div className="flex flex-wrap gap-1.5">
                    {subs.map((s) => (
                      <span key={s.value}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs ${s.active ? "border-slate-200 text-slate-700 bg-slate-50" : "border-slate-100 text-slate-400 line-through bg-white"}`}>
                        {s.label}
                        <button type="button" title={s.active ? "Disable" : "Enable"} onClick={() => toggleSub(it, s.value)} className="text-slate-400 hover:text-slate-700">{s.active ? "◦" : "•"}</button>
                        <button type="button" title="Remove" onClick={() => removeSub(it, s.value)} className="text-rose-400 hover:text-rose-600">×</button>
                      </span>
                    ))}
                    {subs.length === 0 && <span className="text-xs text-slate-400">No sub-types yet.</span>}
                  </div>
                  <div className="flex gap-2">
                    <Input value={subLabel[it.id] || ""} onChange={(e) => setSubLabel((s) => ({ ...s, [it.id]: e.target.value }))}
                      placeholder="Add sub-type…" className="h-8 text-xs"
                      onKeyDown={(e) => e.key === "Enter" && addSub(it)} data-testid={`subtype-input-${it.value}`} />
                    <Button size="sm" className="h-8" onClick={() => addSub(it)} data-testid={`subtype-add-${it.value}`}>Add</Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="py-3 text-xs text-slate-400">Nothing yet.</div>}
      </div>
    </div>
  );
}
